import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PowerSettingsNew as PowerIcon,
  PowerOff as PowerOffIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { API_BASE_URL } from '../../config';

const CouponManagement = ({ authToken }) => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCoupon, setSelectedCoupon] = useState(null);

  const [formData, setFormData] = useState({
    coupon_code: '',
    coupon_name: '',
    description: '',
    discount_type: 'fixed_amount',
    discount_value: '',
    minimum_order_amount: '0',
    usage_limit: '',
    is_active: true
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await fetch(`${API_BASE_URL}/admin/coupons`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      
      if (data.success) {
        setCoupons(data.coupons || []);
      } else {
        setError(data.error || 'Failed to fetch coupons');
      }
    } catch (err) {
      setError('Failed to fetch coupons: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  const handleAddCoupon = () => {
    setEditingCoupon(null);
    setFormData({
      coupon_code: '',
      coupon_name: '',
      description: '',
      discount_type: 'fixed_amount',
      discount_value: '',
      minimum_order_amount: '0',
      usage_limit: '',
      is_active: true
    });
    setDialogOpen(true);
  };

  const handleEditCoupon = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      coupon_code: coupon.coupon_code,
      coupon_name: coupon.coupon_name,
      description: coupon.description || '',
      discount_type: coupon.discount_type || 'fixed_amount',
      discount_value: coupon.discount_value.toString(),
      minimum_order_amount: coupon.minimum_order_amount?.toString() || '0',
      usage_limit: coupon.usage_limit?.toString() || '',
      is_active: coupon.is_active !== undefined ? coupon.is_active : true
    });
    setDialogOpen(true);
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/admin/coupons/${couponId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Coupon deleted successfully');
        fetchCoupons();
      } else {
        setError(data.error || 'Failed to delete coupon');
      }
    } catch (err) {
      setError('Failed to delete coupon: ' + err.message);
    }
  };

  const handleToggleActive = async (coupon) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/coupons/${coupon.id}/toggle`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_active: !coupon.is_active })
      });
      const data = await response.json();
      
      if (data.success) {
        setSuccess(`Coupon ${!coupon.is_active ? 'activated' : 'deactivated'} successfully`);
        fetchCoupons();
      } else {
        setError(data.error || 'Failed to update coupon');
      }
    } catch (err) {
      setError('Failed to update coupon: ' + err.message);
    }
  };

  const handleUpdateUsageLimit = async (couponId, newLimit) => {
    const limit = prompt('Enter new usage limit:', '');
    if (limit === null || limit === '') return;
    
    const numLimit = parseInt(limit);
    if (isNaN(numLimit) || numLimit < 0) {
      setError('Invalid usage limit');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/admin/coupons/${couponId}/update-limit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ usage_limit: numLimit })
      });
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Usage limit updated successfully');
        fetchCoupons();
      } else {
        setError(data.error || 'Failed to update usage limit');
      }
    } catch (err) {
      setError('Failed to update usage limit: ' + err.message);
    }
  };

  const handleUpdateDiscountValue = async (couponId, currentValue) => {
    const value = prompt('Enter new discount amount:', currentValue.toString());
    if (value === null || value === '') return;
    
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < 0) {
      setError('Invalid discount amount');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/admin/coupons/${couponId}/update-discount`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ discount_value: numValue })
      });
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Discount amount updated successfully');
        fetchCoupons();
      } else {
        setError(data.error || 'Failed to update discount amount');
      }
    } catch (err) {
      setError('Failed to update discount amount: ' + err.message);
    }
  };

  const handleMenuOpen = (event, coupon) => {
    setAnchorEl(event.currentTarget);
    setSelectedCoupon(coupon);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCoupon(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!formData.coupon_code.trim()) {
      setError('Coupon code is required');
      return;
    }
    if (!formData.coupon_name.trim()) {
      setError('Coupon name is required');
      return;
    }
    if (!formData.discount_value || parseFloat(formData.discount_value) <= 0) {
      setError('Discount value must be greater than 0');
      return;
    }
    if (!formData.usage_limit || parseInt(formData.usage_limit) <= 0) {
      setError('Usage limit must be greater than 0');
      return;
    }

    try {
      const url = editingCoupon
        ? `${API_BASE_URL}/admin/coupons/${editingCoupon.id}`
        : `${API_BASE_URL}/admin/coupons`;
      
      const method = editingCoupon ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...formData,
          discount_value: parseFloat(formData.discount_value),
          minimum_order_amount: parseFloat(formData.minimum_order_amount) || 0,
          usage_limit: parseInt(formData.usage_limit)
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess(editingCoupon ? 'Coupon updated successfully' : 'Coupon created successfully');
        setDialogOpen(false);
        fetchCoupons();
      } else {
        setError(data.error || 'Failed to save coupon');
      }
    } catch (err) {
      setError('Failed to save coupon: ' + err.message);
    }
  };

  if (loading && coupons.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Coupon Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddCoupon}
          sx={{ bgcolor: '#4b6f44', '&:hover': { bgcolor: '#3a5635' } }}
        >
          Add Coupon
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Coupon Code</TableCell>
              <TableCell>Coupon Name</TableCell>
              <TableCell>Discount</TableCell>
              <TableCell>Usage Limit</TableCell>
              <TableCell>Used</TableCell>
              <TableCell>Remaining</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {coupons.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No coupons found. Click "Add Coupon" to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              coupons.map((coupon) => {
                const remaining = coupon.usage_limit ? coupon.usage_limit - (coupon.used_count || 0) : 'Unlimited';
                const isExhausted = coupon.usage_limit && (coupon.used_count || 0) >= coupon.usage_limit;
                
                return (
                  <TableRow key={coupon.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {coupon.coupon_code}
                      </Typography>
                    </TableCell>
                    <TableCell>{coupon.coupon_name}</TableCell>
                    <TableCell>
                      ₹{parseFloat(coupon.discount_value).toFixed(2)}
                    </TableCell>
                    <TableCell>{coupon.usage_limit || 'Unlimited'}</TableCell>
                    <TableCell>{coupon.used_count || 0}</TableCell>
                    <TableCell>
                      <Chip
                        label={remaining}
                        color={isExhausted ? 'error' : 'success'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={coupon.is_active ? 'Active' : 'Inactive'}
                        color={coupon.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="More options">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, coupon)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedCoupon && (
          <>
            <MenuItem onClick={() => { handleEditCoupon(selectedCoupon); handleMenuClose(); }}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { handleToggleActive(selectedCoupon); handleMenuClose(); }}>
              <ListItemIcon>
                {selectedCoupon.is_active ? (
                  <PowerOffIcon fontSize="small" />
                ) : (
                  <PowerIcon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText>
                {selectedCoupon.is_active ? 'Deactivate' : 'Activate'}
              </ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { handleUpdateUsageLimit(selectedCoupon.id, selectedCoupon.usage_limit); handleMenuClose(); }}>
              <ListItemIcon>
                <RefreshIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Update Usage Limit</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { handleUpdateDiscountValue(selectedCoupon.id, selectedCoupon.discount_value); handleMenuClose(); }}>
              <ListItemIcon>
                <RefreshIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Update Discount Amount</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { handleDeleteCoupon(selectedCoupon.id); handleMenuClose(); }}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingCoupon ? 'Edit Coupon' : 'Add New Coupon'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <TextField
                label="Coupon Code"
                value={formData.coupon_code}
                onChange={(e) => setFormData({ ...formData, coupon_code: e.target.value.toUpperCase() })}
                required
                fullWidth
                disabled={!!editingCoupon}
                helperText={editingCoupon ? "Coupon code cannot be changed" : "This will be the code customers enter"}
              />
              <TextField
                label="Coupon Name"
                value={formData.coupon_name}
                onChange={(e) => setFormData({ ...formData, coupon_name: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="Description (Optional)"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                fullWidth
                multiline
                rows={2}
              />
              <TextField
                label="Discount Amount (₹)"
                type="number"
                value={formData.discount_value}
                onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                required
                fullWidth
                inputProps={{ min: 0, step: 0.01 }}
              />
              <TextField
                label="Minimum Order Amount (₹)"
                type="number"
                value={formData.minimum_order_amount}
                onChange={(e) => setFormData({ ...formData, minimum_order_amount: e.target.value })}
                fullWidth
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Leave 0 for no minimum"
              />
              <TextField
                label="Usage Limit"
                type="number"
                value={formData.usage_limit}
                onChange={(e) => setFormData({ ...formData, usage_limit: e.target.value })}
                required
                fullWidth
                inputProps={{ min: 1 }}
                helperText="Number of times this coupon can be used"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained" sx={{ bgcolor: '#4b6f44', '&:hover': { bgcolor: '#3a5635' } }}>
              {editingCoupon ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default CouponManagement;

